/**
 * AuthService - Authentication State Façade
 * 
 * CENTRALIZES read-only access to authentication state from existing sources.
 * 
 * SOURCE PRIORITY:
 * 1. Redux State (preferred) - via optional parameter for React components
 * 2. sessionStorage (fallback) - via Redux Persist storage for non-React code
 * 
 * MAPPING TO EXISTING SOURCES:
 * - Redux: state.login.user (from useSelector or store.getState())
 * - sessionStorage: persist:root -> Login -> user (parsed JSON)
 * 
 * RULES:
 * - Read-only methods only (no state mutations)
 * - No new logic (uses existing patterns)
 * - No new state (reads from existing sources)
 * - Centralizes existing read patterns
 */

/**
 * User data structure (matches existing Redux state.login.user)
 */
export interface User {
  token: string;
  userId?: string;
  profileId?: string;
  profileName?: string;
  roleId?: string;
  roleName?: string;
  userGroup?: string;
  userName?: string;
  firstName?: string;
  lastName?: string;
  tenantId?: string;
  clientId?: string;
  expiresAt?: string;
  [key: string]: any; // Allow additional fields
}

/**
 * Auth state structure (matches existing Redux state.login)
 */
export interface AuthState {
  user: User | {};
  loading?: boolean;
  error?: string;
  isUserLogout?: boolean;
  errorMsg?: boolean;
}

class AuthService {
  /**
   * Read user from sessionStorage (Redux Persist format)
   * 
   * MAPPING: sessionStorage.getItem('persist:root')
   *   -> JSON.parse -> { Login: "..." }
   *   -> JSON.parse(Login) -> { user: {...} }
   * 
   * This is the same pattern used in:
   * - api_helper.ts: getLoggedinUser()
   * - AuthProtected.tsx: checkStorageAuth()
   */
  private readUserFromStorage(): User | null {
    try {
      const persistedState = sessionStorage.getItem('persist:root');
      if (!persistedState) {
        return null;
      }

      const parsed = JSON.parse(persistedState);
      if (!parsed.Login) {
        return null;
      }

      const loginState = JSON.parse(parsed.Login);
      if (!loginState.user || Object.keys(loginState.user).length === 0) {
        return null;
      }

      return loginState.user as User;
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error("AuthService: Error reading user from sessionStorage:", error);
      }
      return null;
    }
  }

  /**
   * Extract user from Redux state
   * 
   * MAPPING: state.login.user (from useSelector or store.getState())
   * 
   * This matches the pattern used in:
   * - useProfile hook: useSelector((state: any) => state.login?.user)
   * - AuthProtected.tsx: useSelector((state: any) => state.login?.user)
   */
  private extractUserFromReduxState(reduxState: any): User | null {
    try {
      const loginState = reduxState?.login;
      if (!loginState) {
        return null;
      }

      const user = loginState.user;
      if (!user || Object.keys(user).length === 0) {
        return null;
      }

      return user as User;
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error("AuthService: Error extracting user from Redux state:", error);
      }
      return null;
    }
  }

  /**
   * Get user from available sources
   * 
   * PRIORITY:
   * 1. Redux state (if provided) - preferred source
   * 2. sessionStorage - fallback for non-React code
   * 
   * @param reduxState - Optional Redux state (for React components using useSelector)
   * @returns User object or null
   */
  private getUserFromSources(reduxState?: any): User | null {
    // Try Redux state first (preferred)
    if (reduxState) {
      const user = this.extractUserFromReduxState(reduxState);
      if (user) {
        return user;
      }
    }

    // Fallback to sessionStorage (for non-React code)
    return this.readUserFromStorage();
  }

  /**
   * Check if user is authenticated
   * 
   * MAPPING: Same logic as existing checks:
   * - AuthProtected.tsx: hasAnyAuth = userProfile || hasReduxAuth || !!storageToken || !!token
   * - api_helper.ts: isAuthTokenValid() checks user && token
   * 
   * @param reduxState - Optional Redux state (for React components)
   * @returns true if user exists and has token
   */
  isAuthenticated(reduxState?: any): boolean {
    const user = this.getUserFromSources(reduxState);
    
    if (!user) {
      return false;
    }

    // Check if user has token (standardized field name)
    const token = user.token;
    return !!token;
  }

  /**
   * Get current user object
   * 
   * MAPPING: Returns same structure as:
   * - useProfile hook: userProfile
   * - api_helper.ts: getLoggedinUser()
   * - AuthProtected.tsx: userProfile / reduxUser
   * 
   * @param reduxState - Optional Redux state (for React components)
   * @returns User object or null
   */
  getUser(reduxState?: any): User | null {
    const user = this.getUserFromSources(reduxState);
    return user;
  }

  /**
   * Get authentication token
   * 
   * MAPPING: Returns same value as:
   * - useProfile hook: token (from reduxUser.token)
   * - api_helper.ts: getAuthToken() (from sessionStorage)
   * - AuthProtected.tsx: token / reduxToken / storageToken
   * 
   * Standardized to use 'token' field (not 'jwt' - legacy)
   * 
   * @param reduxState - Optional Redux state (for React components)
   * @returns Token string or null
   */
  getToken(reduxState?: any): string | null {
    const user = this.getUserFromSources(reduxState);
    
    if (!user) {
      return null;
    }

    // Standardize: use 'token' field only (jwt is legacy, removed)
    return user.token || null;
  }
}

// Export singleton instance
export const authService = new AuthService();

// Export class for testing if needed
export default AuthService;

// Re-export auth API functions for compatibility with existing thunks
// These functions are actually in auth_api_helper.ts, but thunks import from here
export { loginByEmail as requestEmailOtp, verifyEmailOtp } from "../helpers/auth_api_helper";
